import { useCallback } from "react";

interface Props {
  /**
   * Which neighbour this handle sizes. "left" → the panel on its left grows as
   * you drag right; "right" → the panel on its right grows as you drag left
   * (so the divider follows the cursor in both cases).
   */
  side: "left" | "right";
  /** Grid-area class — "rz-left" | "rz-right". */
  className: string;
  width: number;
  onResize: (width: number) => void;
  min: number;
  max: number;
  /** Double-click restores this width. */
  defaultWidth: number;
  label: string;
}

/**
 * A 6px vertical drag strip that resizes the column next to it. Driven by
 * pointer events (mouse + touch), with arrow-key nudging and double-click reset
 * for keyboard/accessibility users. The parent owns `width` and persistence;
 * this only reports new values back through `onResize`.
 */
export function Resizer({ side, className, width, onResize, min, max, defaultWidth, label }: Props) {
  const clamp = useCallback((w: number) => Math.max(min, Math.min(max, w)), [min, max]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      const el = e.currentTarget;
      el.classList.add("dragging");
      document.body.classList.add("resizing");

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        onResize(clamp(side === "left" ? startW + dx : startW - dx));
      };
      const onUp = () => {
        el.classList.remove("dragging");
        document.body.classList.remove("resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [side, width, onResize, clamp],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const step = (e.shiftKey ? 32 : 8) * (side === "left" ? 1 : -1);
      if (e.key === "ArrowRight") { e.preventDefault(); onResize(clamp(width + step)); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); onResize(clamp(width - step)); }
    },
    [side, width, onResize, clamp],
  );

  return (
    <div
      className={`resizer ${className}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onResize(defaultWidth)}
      title={`${label} — drag, arrow-keys, or double-click to reset`}
    />
  );
}
